import { toast } from 'sonner'
import { classifyError, isApiError } from '@/api/errors'

/**
 * Toast conventions (UI_PLAN Component C):
 * success green 4s · error red persists + shows error code · warning amber 8s · info blue 4s.
 */
export function toastSuccess(message: string) {
  toast.success(message, { duration: 4_000 })
}

export function toastInfo(message: string) {
  toast.info(message, { duration: 4_000 })
}

export function toastWarning(message: string) {
  toast.warning(message, { duration: 8_000 })
}

export function toastError(message: string, options?: { code?: string }) {
  toast.error(message, {
    duration: Infinity,
    closeButton: true,
    ...(options?.code ? { description: `Error code: ${options.code}` } : {}),
  })
}

/**
 * Default handler for mutation errors that no screen handled specifically.
 * Field/blocking/reauth classes are expected to be handled closer to the
 * screen — reaching here still produces a sensible toast rather than silence.
 */
export function toastApiError(error: unknown) {
  if (!isApiError(error)) {
    toastError('Something went wrong. Please try again.')
    return
  }
  switch (classifyError(error)) {
    case 'degraded':
      toastWarning(
        'A backing service is temporarily unavailable — your account is unaffected. Try again in a minute.',
      )
      break
    case 'conflict':
      toastWarning(
        error.message || 'This record changed while you were editing. Reload and try again.',
      )
      break
    case 'rate-limit': {
      const wait = error.retryAfterSeconds ? ` Try again in ${error.retryAfterSeconds}s.` : ''
      toastWarning(`You're doing that too often.${wait}`)
      break
    }
    case 'permission':
      toastError("You don't have permission to do this.", { code: error.errorCode })
      break
    default:
      toastError(error.message, { code: error.errorCode })
  }
}
